import { Component, OnInit, ElementRef, ViewChild, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule, AbstractControl, ValidatorFn } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatChipsModule } from '@angular/material/chips';
import { MatSliderModule } from '@angular/material/slider';
import { MatTabsModule } from '@angular/material/tabs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Router, ActivatedRoute } from '@angular/router';
import { compare } from 'fast-json-patch';
import { ToastrService } from 'ngx-toastr';

import { PromptsDTO } from '../../models/promts.dto';
import { PromptType } from '../../models/prompt-type.enum';
import { PromtsManagemetServices } from '../../services/promts-managemet.services';
import { LlmTestService } from '../../services/llm-test.service';
import { TestResultDTO } from '../../models/test-result.dto';
import { OrganisationDTO } from '../../../../../DTO/Organisation/OrganisationDTO';
import {PermissionService} from "../../../../../../core/authorization/permission.service";
import {LocalizationPipe} from "../../../../../pipe/localization-pipe";
import {LocalizationService} from "../../../../../services/localizationService";
import { ResourceManagementComponent } from '../../../file/resource-management/resource-management.component';

export enum PromptFormMode {
  CREATE = 'create',
  EDIT = 'edit',
  VIEW = 'view'
}

// Test için gerekli enum'lar
export enum LLMModel {
  GEMINI_25_PRO_PREVIEW = 'gemini-2.5-pro-preview-03-25',
  GEMINI_20_FLASH = 'gemini-2.0-flash',
  GEMINI_20_FLASH_LITE = 'gemini-2.0-flash-lite',
  GEMINI_15_PRO = 'gemini-1.5-pro',
  GPT_45_PREVIEW = 'gpt-4.5-preview',
  GPT_4O = 'gpt-4o',
  GPT_O1 = 'o1',
  GPT_O1_PRO = 'o1-pro',
  GPT_O3_MINI = 'o3-mini'
}

export enum ToneType {
  FORMAL = 'FORMAL',
  CASUAL = 'CASUAL',
  NEUTRAL = 'NEUTRAL',
  EXPLANATORY = 'EXPLANATORY'
}

@Component({
  selector: 'app-prompt-create',
  templateUrl: './prompt-create.component.html',
  styleUrls: ['./prompt-create.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatRadioModule,
    MatChipsModule,
    MatSliderModule,
    MatTabsModule,
    LocalizationPipe,
    ResourceManagementComponent
  ]
})
export class PromptCreateComponent implements OnInit {
  @ViewChild('tagInput') tagInput: ElementRef<HTMLInputElement>;
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
  @ViewChild('resourceManagement') resourceManagement: any;

  promptForm: FormGroup;
  testMode = false;
  testLoading = false;
  testResult = '';
  testResultDto: TestResultDTO | null = null;
  selectedType = '';
  organisationComponent:boolean;
  selectedOrganisationId:number;
  saving = false;

  // Form modu (oluşturma, düzenleme, görüntüleme)
  formMode: PromptFormMode = PromptFormMode.CREATE;
  PromptFormMode = PromptFormMode;

  // Düzenlenecek veya görüntülenecek prompt
  currentPrompt: PromptsDTO | null = null;
  originalPrompt: PromptsDTO | null = null;

  // Tag işlemleri için
  tags: string[] = [];
  separatorKeysCodes: number[] = [ENTER, COMMA];

  // Dosya yükleme için
  selectedFiles: File[] = [];
  isDragOver = false;
  selectedDataSourceIds: number[] = [];
  selectedTabIndex = 0; // Dosya sekmesi için

  // Prompt tipleri - radio button için
  promptTypes = [
    { value: PromptType.DOCUMENT_PARSER, display: 'Prompts.Categories.DocumentParser' },
    { value: PromptType.MULTIMEDIA_ANALYZER, display: 'Prompts.Categories.MultimediaAnalyzer' },
    { value: PromptType.ORGANIZATION, display: 'Prompts.Categories.Organization' },
    { value: PromptType.WEB_PAGE, display: 'Prompts.Categories.WebPage' },
    { value: PromptType.WEB_SERVICES, display: 'Prompts.Categories.WebServices' },
    { value: PromptType.INTEGRATION, display: 'Prompts.Categories.Integration' },
    { value: PromptType.TEXT, display: 'Prompts.Categories.Text' }
  ];

  // Test için model seçenekleri
  availableModels = [
    { value: LLMModel.GEMINI_25_PRO_PREVIEW, display: 'Gemini 2.5 Pro Preview' },
    { value: LLMModel.GEMINI_20_FLASH, display: 'Gemini 2.0 Flash' },
    { value: LLMModel.GEMINI_20_FLASH_LITE, display: 'Gemini 2.0 Flash-Lite' },
    { value: LLMModel.GEMINI_15_PRO, display: 'Gemini 1.5 Pro' },
    { value: LLMModel.GPT_45_PREVIEW, display: 'GPT-4.5 Preview' },
    { value: LLMModel.GPT_4O, display: 'GPT-4o' },
    { value: LLMModel.GPT_O1, display: 'GPT-o1' },
    { value: LLMModel.GPT_O1_PRO, display: 'GPT-o1-Pro' },
    { value: LLMModel.GPT_O3_MINI, display: 'GPT o3-mini' }
  ];

  // Ton seçenekleri
  availableTones = [
    { value: ToneType.FORMAL, display: 'Prompts.Tones.Formal' },
    { value: ToneType.CASUAL, display: 'Prompts.Tones.Casual' },
    { value: ToneType.NEUTRAL, display: 'Prompts.Tones.Neutral' },
    { value: ToneType.EXPLANATORY, display: 'Prompts.Tones.Explanatory' }
  ];

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private toastr: ToastrService,
    private promtsService: PromtsManagemetServices,
    private llmTestService: LlmTestService,
    private router: Router,
    private route: ActivatedRoute,
    public permissionService: PermissionService,
    private localizationService: LocalizationService
  ) {
    this.promptForm = this.fb.group({
      promptName: ['', [Validators.required, this.noWhitespaceValidator()]],
      promptText: ['', [Validators.required, Validators.minLength(5), this.noWhitespaceValidator()]],
      description: [''],
      type: ['', Validators.required],
      // Test alanları
      testModel: [LLMModel.GEMINI_20_FLASH_LITE],
      temperature: [0.7, [Validators.min(0), Validators.max(1)]],
      tone: [ToneType.FORMAL],
      userQuestion: ['']
    });
  }

  ngOnInit(): void {
    // URL parametrelerini kontrol et
    this.route.params.subscribe(params => {
      const promptId = params['id'];
      const mode = params['mode'];

      if (promptId) {
        // ID varsa, düzenleme veya görüntüleme modu
        if (mode === 'edit') {
          this.formMode = PromptFormMode.EDIT;
        } else if (mode === 'view') {
          this.formMode = PromptFormMode.VIEW;
        }

        // Prompt verilerini yükle
        this.loadPromptData(promptId);
      } else {
        // ID yoksa, oluşturma modu
        this.formMode = PromptFormMode.CREATE;
      }
    });

    this.route.queryParams.subscribe(queryParams => {
      this.organisationComponent = queryParams['organisationComponent'] === 'true';
      this.selectedOrganisationId = +queryParams['selectedOrganisationId'];
    });
  }

  loadPromptData(promptId: number): void {
    this.promtsService.getPromptById(promptId).subscribe(
      (res) => {
        if (res && res.data) {
          // Asıl veriyi kopyala
          this.originalPrompt = new PromptsDTO();
          this.originalPrompt.bindData(res.data);

          // Çalışılacak kopya
          this.currentPrompt = new PromptsDTO();
          this.currentPrompt.bindData(res.data);

          // Değerleri forma doldur
          const formFields = {
            promptName: this.currentPrompt.promptName || '',
            promptText: this.currentPrompt.promptText || '',
            description: this.currentPrompt.description || '',
            type: this.currentPrompt.type || ''
          };

          this.promptForm.patchValue(formFields);

          // Tip değerini ayarla
          this.selectedType = this.currentPrompt.type || '';

          // Etiketleri yükle
          if (this.currentPrompt.tags && this.currentPrompt.tags.length > 0) {
            this.tags = [...this.currentPrompt.tags];
          }

          // Görüntüleme modunda formu devre dışı bırak
          if (this.formMode === PromptFormMode.VIEW) {
            this.promptForm.disable();
          }
        } else {
          this.toastr.error(
            this.localizationService.getLocalizationValue('Prompts.LoadError'),
            this.localizationService.getLocalizationValue('Common.Error')
          );
          this.router.navigate(['/apps/prompt-management']);
        }
      },
      (error) => {
        console.error('Prompt yüklenirken hata oluştu:', error);
        this.toastr.error(
          this.localizationService.getLocalizationValue('Prompts.LoadError'),
          this.localizationService.getLocalizationValue('Common.Error')
        );
        this.router.navigate(['/apps/prompt-management']);
      }
    );
  }

  // Dosya yükleme metodları
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(files);
    }
  }

  onFileSelect(event: any): void {
    const files = event.target.files;
    if (files) {
      this.handleFiles(files);
    }
  }

  private handleFiles(files: FileList): void {
    const allowedTypes = ['application/pdf', 'text/plain', 'text/markdown', 'audio/mpeg', 'audio/mp3'];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (allowedTypes.includes(file.type) || file.name.endsWith('.md') || file.name.endsWith('.txt')) {
        this.selectedFiles.push(file);
      } else {
        this.toastr.warning(
          `${this.localizationService.getLocalizationValue('Prompts.UnsupportedFileType')}: ${file.name}`,
          this.localizationService.getLocalizationValue('Common.Warning')
        );
      }
    }
  }

  removeFile(index: number): void {
    this.removeUploadedFile(index);
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  onDataSourcesSelected(fileIds: number[]): void {
    this.selectedDataSourceIds = fileIds;
  }

  // Yüklenen dosyayı kaldır
  removeUploadedFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  // Mevcut dosya seçimini kaldır (ResourceManagement'tan gelen)
  removeSelectedDataSource(fileId: number): void {
    this.selectedDataSourceIds = this.selectedDataSourceIds.filter(id => id !== fileId);
    // Resource management'taki checkbox'ı da kaldır
    if (this.resourceManagement) {
      this.resourceManagement.removeFileFromSelection(fileId);
    }
  }

  // Tag işlemleri
  addTag(event: any): void {
    if (this.formMode === PromptFormMode.VIEW) return;

    const input = event.input;
    const value = (event.value || '').trim();

    // Tag ekle
    if (value) {
      this.tags.push(value);
    }

    // Input'u sıfırla
    if (input) {
      input.value = '';
    }
  }

  removeTag(tag: string): void {
    if (this.formMode === PromptFormMode.VIEW) return;

    const index = this.tags.indexOf(tag);
    if (index >= 0) {
      this.tags.splice(index, 1);
    }
  }

  onTypeChange(value: string): void {
    if (this.formMode === PromptFormMode.VIEW) return;

    this.selectedType = value;
    this.promptForm.get('type')?.setValue(value);
  }

  onCancel(): void {
    this.navigateAfterAction();
  }

  onTest(): void {
    if (this.promptForm.valid) {
      this.testMode = true;
      this.testLoading = true;
      this.testResult = '';
      this.testResultDto = null;
      const testParams = this.prepareTestParameters();
      this.llmTestService.testPromptWithParameters(testParams).subscribe(
        (result) => {
          this.testResult = result.response;
          this.testResultDto = new TestResultDTO();
          this.testResultDto.bindData(result);
          this.testLoading = false;
        },
        (error) => {
          this.testResult = 'Test sırasında bir hata oluştu. Lütfen tekrar deneyin.';
          this.testResultDto = null;
          this.testLoading = false;
          this.toastr.error(
            this.localizationService.getLocalizationValue('Prompts.TestError'),
            this.localizationService.getLocalizationValue('Common.Error')
          );
        }
      );
    }
  }

  private prepareTestParameters(): any {
    const formValue = this.promptForm.value;

    // Temel parametreler
    const testParams: any = {
      prompt: formValue.promptText.trim(),
      question: formValue.userQuestion?.trim() || null,
      model_ids: [formValue.testModel],
      temperature: formValue.temperature,
      tone: formValue.tone,
      organization_id: this.selectedOrganisationId || (window.globalActiveUser?.organisationAccount?.id || null)
    };

    // Mevcut dosyalardan seçilenler varsa data_ids ekle
    if (this.selectedDataSourceIds.length > 0) {
      testParams.data_ids = this.selectedDataSourceIds;
    }

    // Yüklenen dosyalar varsa files ekle
    if (this.selectedFiles.length > 0) {
      testParams.files = this.selectedFiles;
    }

    return testParams;
  }

  onSave(): void {
    if (this.promptForm.valid) {
      const formValue = this.promptForm.value;

      const trimmedValues = {
        ...formValue,
        promptName: formValue.promptName.trim(),
        promptText: formValue.promptText.trim(),
        description: formValue.description ? formValue.description.trim() : ''
      };
      if (this.formMode === PromptFormMode.EDIT && this.currentPrompt && this.originalPrompt) {
        this.currentPrompt.promptName = trimmedValues.promptName;
        this.currentPrompt.promptText = trimmedValues.promptText;
        this.currentPrompt.description = trimmedValues.description;
        this.currentPrompt.type = trimmedValues.type;
        this.currentPrompt.tags = [...this.tags];

        const patchOperations = compare(this.originalPrompt, this.currentPrompt);

        this.saving = true;
        this.promtsService.patchOrganizationPromts(this.currentPrompt.id, patchOperations).subscribe(
          (response) => {
            this.toastr.success(
              this.localizationService.getLocalizationValue('Prompts.UpdateSuccess'),
              this.localizationService.getLocalizationValue('Common.Success')
            );
            this.saving = false;
            this.navigateAfterAction();
          },
          (error) => {
            console.error('Güncelleme hatası:', error);
            this.toastr.error(
              this.localizationService.getLocalizationValue('Prompts.UpdateError'),
              this.localizationService.getLocalizationValue('Common.Error')
            );
            this.saving = false;
          }
        );
      } else {
        // Yeni prompt oluştur
        let prompt = new PromptsDTO();
        prompt.id = this.generateTempId();
        prompt.promptName = trimmedValues.promptName;
        prompt.promptText = trimmedValues.promptText;
        prompt.description = trimmedValues.description;
        prompt.type = trimmedValues.type;
        prompt.tags = [...this.tags];
        if (this.selectedOrganisationId) {
          prompt.organisation = new OrganisationDTO();
          prompt.organisation.id = this.selectedOrganisationId;
        } else if (window.globalActiveUser && window.globalActiveUser.organisationAccount && window.globalActiveUser.organisationAccount.id) {
          prompt.organisation = new OrganisationDTO();
          prompt.organisation.id = window.globalActiveUser.organisationAccount.id;
        }
        this.saving = true;

        this.promtsService.create(prompt).subscribe(
          (res) => {
            this.toastr.success(
              this.localizationService.getLocalizationValue('Prompts.CreateSuccess'),
              this.localizationService.getLocalizationValue('Common.Success')
            );
            this.saving = false;
            this.navigateAfterAction();
          },
          (error) => {
            this.toastr.error(
              this.localizationService.getLocalizationValue('Prompts.CreateError') + (error.message ? `: ${error.message}` : ''),
              this.localizationService.getLocalizationValue('Common.Error')
            );
            this.saving = false;
          }
        );
      }
    } else {
      // Form geçerli değilse, validasyon hatalarını göstermek için tüm alanları dirty et
      Object.keys(this.promptForm.controls).forEach(key => {
        const control = this.promptForm.get(key);
        control?.markAsDirty();
        control?.updateValueAndValidity();
      });
    }
  }

  clearTestResults(): void {
    this.testResult = '';
    this.testResultDto = null;
    this.testMode = false;
  }

  getSelectedModelName(): string {
    const selectedModel = this.availableModels.find(m =>
      m.value === this.promptForm.get('testModel')?.value
    );
    return selectedModel ? selectedModel.display : '';
  }

  getSelectedToneName(): string {
    const selectedTone = this.availableTones.find(t =>
      t.value === this.promptForm.get('tone')?.value
    );
    return selectedTone ? this.localizationService.getLocalizationValue(selectedTone.display) : '';
  }

  get pageTitle(): string {
    switch (this.formMode) {
      case PromptFormMode.EDIT:
        return 'Prompts.Edit.Title';
      case PromptFormMode.VIEW:
        return 'Prompts.View.Title';
      default:
        return 'Prompts.Create.Title';
    }
  }

  get isViewOnly(): boolean {
    return this.formMode === PromptFormMode.VIEW;
  }

  getTypeDisplay(typeValue: string): string {
    const type = this.promptTypes.find(t => t.value === typeValue);
    return type ? this.localizationService.getLocalizationValue(type.display) : this.localizationService.getLocalizationValue('Prompts.Categories.NotSelected');
  }



  private generateTempId(): number {
    return Math.floor(Math.random() * 1000) + 3;
  }

  get hasChanges(): boolean {
    if (this.formMode !== PromptFormMode.EDIT || !this.originalPrompt || !this.currentPrompt) {
      return false;
    }
    const tempPrompt = new PromptsDTO();
    tempPrompt.bindData(this.originalPrompt);

    const formValue = this.promptForm.value;
    tempPrompt.promptName = formValue.promptName;
    tempPrompt.promptText = formValue.promptText;
    tempPrompt.description = formValue.description;
    tempPrompt.type = formValue.type;
    tempPrompt.tags = [...this.tags];

    // Orijinal nesne ile geçici nesne arasındaki farkları bul
    const patches = compare(this.originalPrompt, tempPrompt);

    // Değişiklik var mı?
    return patches.length > 0;
  }

  noWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const isWhitespace = control.value && control.value.toString().trim().length === 0;
      return isWhitespace ? { 'whitespace': true } : null;
    };
  }

  private navigateAfterAction(): void {
    if (this.organisationComponent && this.selectedOrganisationId) {
      this.router.navigate(['/apps/organization/detail', this.selectedOrganisationId]);
    } else {
      this.router.navigate(['/apps/prompt-management']);
    }
  }

  protected readonly window = window;
}
